import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'chronologicalAge', 'sex', 'restingHR', 'systolic', 'diastolic',
    'fastingGlucose', 'hdl', 'exerciseFrequency', 'sleepQuality',
    'smokingStatus', 'alcoholConsumption',
    'biologicalAge', 'differenceContainer', 'differenceDot', 'differenceValue',
    'differenceDescription', 'keyInsight',
    'hrContribution', 'bpContribution', 'glucoseContribution', 'hdlContribution',
    'exerciseContribution', 'sleepContribution', 'smokingContribution', 'alcoholContribution'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const chronologicalAge = parseFloat(this.chronologicalAgeTarget.value) || 35
    const sex = this.sexTargets.find((r) => r.checked)?.value || 'male'
    const restingHR = parseFloat(this.restingHRTarget.value) || 68
    const systolic = parseFloat(this.systolicTarget.value) || 120
    const diastolic = parseFloat(this.diastolicTarget.value) || 80
    const fastingGlucose = parseFloat(this.fastingGlucoseTarget.value) || 92
    const hdl = parseFloat(this.hdlTarget.value) || 55
    const exerciseFrequency = parseInt(this.exerciseFrequencyTarget.value, 10) || 2
    const sleepQuality = parseInt(this.sleepQualityTarget.value, 10) || 2
    const smokingStatus = parseInt(this.smokingStatusTarget.value, 10) || 0
    const alcoholConsumption = parseInt(this.alcoholConsumptionTarget.value, 10) || 0

    // Calculate contributions from each factor
    // eslint-disable-next-line max-len
    const contributions = this.calculateContributions(restingHR, systolic, diastolic, fastingGlucose, hdl, exerciseFrequency, sleepQuality, smokingStatus, alcoholConsumption, sex)

    // Sum all contributions
    const totalAdjustment = Object.values(contributions).reduce((sum, val) => sum + val, 0)
    const biologicalAge = Math.round(chronologicalAge + totalAdjustment)
    const difference = biologicalAge - chronologicalAge

    // Update displays
    this.biologicalAgeTarget.textContent = biologicalAge
    this.updateContributionDisplays(contributions)
    this.updateDifferenceDisplay(difference)
    this.updateKeyInsight(difference, contributions)
  }

  calculateContributions(
    restingHR, systolic, diastolic, fastingGlucose, hdl,
    exerciseFrequency, sleepQuality, smokingStatus, alcoholConsumption, sex
  ) {
    // Resting Heart Rate contribution
    // Optimal: 55-65 BPM, each 5 BPM deviation = ~0.5 years
    let hrContribution = 0
    if (restingHR < 50) {
      hrContribution = -1.5 // Very fit
    } else if (restingHR <= 55) {
      hrContribution = -1.5
    } else if (restingHR <= 60) {
      hrContribution = -1
    } else if (restingHR <= 65) {
      hrContribution = -0.5
    } else if (restingHR <= 70) {
      hrContribution = 0
    } else if (restingHR <= 75) {
      hrContribution = 0.5
    } else if (restingHR <= 80) {
      hrContribution = 1
    } else if (restingHR <= 85) {
      hrContribution = 1.5
    } else {
      hrContribution = 2 + (restingHR - 85) * 0.1
    }

    // Blood Pressure contribution
    // Optimal: <120/80, elevated BP adds years
    let bpContribution = 0
    const _systolicDev = Math.max(0, systolic - 120)
    const _diastolicDev = Math.max(0, diastolic - 80)
    if (systolic < 100 && diastolic < 70) {
      bpContribution = -0.5 // Optimal low BP (if not hypotensive)
    } else if (systolic < 120 && diastolic < 80) {
      bpContribution = -0.5 // Normal
    } else if (systolic < 130 && diastolic < 85) {
      bpContribution = 0.5 // Elevated
    } else if (systolic < 140 || diastolic < 90) {
      bpContribution = 1.5 // Stage 1
    } else if (systolic < 160 || diastolic < 100) {
      bpContribution = 3 // Stage 2
    } else {
      bpContribution = 5 // Severe
    }

    // Fasting Glucose contribution
    // Optimal: 70-90 mg/dL
    let glucoseContribution = 0
    if (fastingGlucose < 70) {
      glucoseContribution = 0.5 // Low, could indicate issues
    } else if (fastingGlucose <= 80) {
      glucoseContribution = -1 // Optimal
    } else if (fastingGlucose <= 90) {
      glucoseContribution = -0.5 // Good
    } else if (fastingGlucose <= 100) {
      glucoseContribution = 0.5 // Normal but higher
    } else if (fastingGlucose <= 110) {
      glucoseContribution = 1 // Impaired fasting glucose
    } else if (fastingGlucose <= 125) {
      glucoseContribution = 2 // Prediabetic range
    } else {
      glucoseContribution = 4 // Diabetic range
    }

    // HDL Cholesterol contribution
    // Higher is better, optimal >60 mg/dL
    let hdlContribution = 0
    const _hdlOptimal = sex === 'female' ? 55 : 45 // Women naturally have higher HDL
    if (hdl >= 80) {
      hdlContribution = -2 // Excellent
    } else if (hdl >= 60) {
      hdlContribution = -1 // Good
    } else if (hdl >= 50) {
      hdlContribution = 0 // Normal
    } else if (hdl >= 40) {
      hdlContribution = 1 // Low
    } else {
      hdlContribution = 2.5 // Very low
    }

    // Exercise contribution
    // More exercise = younger biological age
    const exerciseContributions = {
      0: 3, // Sedentary adds 3 years
      1: 1, // Light exercise adds 1 year
      2: -1, // Moderate exercise subtracts 1 year
      3: -2.5, // Active subtracts 2.5 years
      4: -4 // Very active subtracts 4 years
    }
    const exerciseContribution = exerciseContributions[exerciseFrequency] || 0

    // Sleep Quality contribution
    const sleepContributions = {
      0: 3, // Poor sleep adds 3 years
      1: 1, // Fair sleep adds 1 year
      2: -0.5, // Good sleep subtracts 0.5 years
      3: -1.5 // Excellent sleep subtracts 1.5 years
    }
    const sleepContribution = sleepContributions[sleepQuality] || 0

    // Smoking Status contribution
    const smokingContributions = {
      0: -0.5, // Never smoked
      1: 0.5, // Former smoker, quit 5+ years
      2: 2, // Former smoker, quit <5 years
      3: 4, // Current occasional smoker
      4: 8 // Current daily smoker
    }
    const smokingContribution = smokingContributions[smokingStatus] || 0

    // Alcohol Consumption contribution
    const alcoholContributions = {
      0: -0.5, // None/rarely - slight benefit
      1: 0, // Moderate - neutral
      2: 2, // Heavy - adds 2 years
      3: 4 // Very heavy - adds 4 years
    }
    const alcoholContribution = alcoholContributions[alcoholConsumption] || 0

    return {
      hr: hrContribution,
      bp: bpContribution,
      glucose: glucoseContribution,
      hdl: hdlContribution,
      exercise: exerciseContribution,
      sleep: sleepContribution,
      smoking: smokingContribution,
      alcohol: alcoholContribution
    }
  }

  updateContributionDisplays(contributions) {
    this.hrContributionTarget.textContent = this.formatContribution(contributions.hr)
    this.bpContributionTarget.textContent = this.formatContribution(contributions.bp)
    this.glucoseContributionTarget.textContent = this.formatContribution(contributions.glucose)
    this.hdlContributionTarget.textContent = this.formatContribution(contributions.hdl)
    this.exerciseContributionTarget.textContent = this.formatContribution(contributions.exercise)
    this.sleepContributionTarget.textContent = this.formatContribution(contributions.sleep)
    this.smokingContributionTarget.textContent = this.formatContribution(contributions.smoking)
    this.alcoholContributionTarget.textContent = this.formatContribution(contributions.alcohol)

    // Color the contributions
    this.colorContribution(this.hrContributionTarget, contributions.hr)
    this.colorContribution(this.bpContributionTarget, contributions.bp)
    this.colorContribution(this.glucoseContributionTarget, contributions.glucose)
    this.colorContribution(this.hdlContributionTarget, contributions.hdl)
    this.colorContribution(this.exerciseContributionTarget, contributions.exercise)
    this.colorContribution(this.sleepContributionTarget, contributions.sleep)
    this.colorContribution(this.smokingContributionTarget, contributions.smoking)
    this.colorContribution(this.alcoholContributionTarget, contributions.alcohol)
  }

  formatContribution(value) {
    if (value === 0) return '0'
    const sign = value > 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}`
  }

  colorContribution(element, value) {
    element.classList.remove('text-green-600', 'text-red-600', 'text-gray-600')
    if (value < 0) {
      element.classList.add('text-green-600')
    } else if (value > 0) {
      element.classList.add('text-red-600')
    } else {
      element.classList.add('text-gray-600')
    }
  }

  updateDifferenceDisplay(difference) {
    const _absDiff = Math.abs(difference)
    this.differenceValueTarget.textContent = (difference >= 0 ? '+' : '') + difference

    let colorClass; let bgClass; let borderClass; let dotColor; let
      description

    if (difference <= -5) {
      colorClass = 'text-green-700'
      bgClass = 'bg-green-50'
      borderClass = 'border-green-500'
      dotColor = 'bg-green-500'
      description = 'Excellent! Your biological age is significantly younger than your chronological age. ' +
        'Your lifestyle choices are paying off.'
    } else if (difference <= -2) {
      colorClass = 'text-green-600'
      bgClass = 'bg-green-50'
      borderClass = 'border-green-500'
      dotColor = 'bg-green-500'
      description = 'Great! You are aging more slowly than average. Keep up the healthy habits.'
    } else if (difference <= 2) {
      colorClass = 'text-gray-700'
      bgClass = 'bg-gray-50'
      borderClass = 'border-gray-400'
      dotColor = 'bg-gray-400'
      description = 'Your biological age is roughly equal to your chronological age. This is average.'
    } else if (difference <= 5) {
      colorClass = 'text-yellow-700'
      bgClass = 'bg-yellow-50'
      borderClass = 'border-yellow-500'
      dotColor = 'bg-yellow-500'
      description = 'Your biological age is slightly higher than your chronological age. ' +
        'Consider lifestyle improvements.'
    } else if (difference <= 10) {
      colorClass = 'text-orange-700'
      bgClass = 'bg-orange-50'
      borderClass = 'border-orange-500'
      dotColor = 'bg-orange-500'
      description = 'Your biological age is notably higher than your chronological age. ' +
        'Lifestyle changes are strongly recommended.'
    } else {
      colorClass = 'text-red-700'
      bgClass = 'bg-red-50'
      borderClass = 'border-red-500'
      dotColor = 'bg-red-500'
      description = 'Your biological age is significantly higher than your chronological age. ' +
        'Please consult with a healthcare provider about lifestyle interventions.'
    }

    this.differenceContainerTarget.className = `p-6 border-2 ${borderClass} ${bgClass} mb-6`
    this.differenceDotTarget.className = `w-4 h-4 ${dotColor}`
    this.differenceValueTarget.className = `font-mono text-2xl font-bold ${colorClass}`
    this.differenceDescriptionTarget.textContent = description
  }

  updateKeyInsight(difference, contributions) {
    // Find the biggest positive contributor (aging factor)
    const positiveContributors = Object.entries(contributions)
      .filter(([_, value]) => value > 0)
      .sort((a, b) => b[1] - a[1])

    // Find the biggest negative contributor (anti-aging factor)
    const negativeContributors = Object.entries(contributions)
      .filter(([_, value]) => value < 0)
      .sort((a, b) => a[1] - b[1])

    const factorNames = {
      hr: 'resting heart rate',
      bp: 'blood pressure',
      glucose: 'fasting glucose',
      hdl: 'HDL cholesterol',
      exercise: 'exercise habits',
      sleep: 'sleep quality',
      smoking: 'smoking status',
      alcohol: 'alcohol consumption'
    }

    let insight = ''
    if (difference <= -3 && negativeContributors.length > 0) {
      const topFactor = factorNames[negativeContributors[0][0]]
      insight = `Your ${topFactor} is your strongest anti-aging factor. Keep up the great work!`
    } else if (difference >= 3 && positiveContributors.length > 0) {
      const topFactor = factorNames[positiveContributors[0][0]]
      insight = `Improving your ${topFactor} could have the biggest impact on reducing your biological age.`
    } else if (positiveContributors.length > 0 && negativeContributors.length > 0) {
      const worstFactor = factorNames[positiveContributors[0][0]]
      const bestFactor = factorNames[negativeContributors[0][0]]
      insight = `Your ${bestFactor} is your strongest asset. Focus on improving your ${worstFactor} for more gains.`
    } else {
      insight = 'This is an estimate based on simplified calculations. ' +
        'For accurate biological age assessment, consider epigenetic testing.'
    }

    this.keyInsightTarget.textContent = insight
  }
}
